import { Button, CheckboxField, DrawerBody, DrawerFooter, Icon, SubmitButton, TextField } from "@dgs/core";
import { FieldArray, FieldArrayRenderProps, Formik } from "formik";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CreatableCompanySelect } from "../../companies/CreatableCompanySelect";
import { ICompanyOptionResource } from "../../shared/types";
import { AdditionalList, AdditionalListItem, EmptyAdditionalList } from "./AdditionalList";
import { CreateContactStepForm, DrawerFormWithSubmitter, FormActions, SubmitAction } from "./Form";

interface CompanyFormValues {
	company: ICompanyOptionResource | null;
	position: string;
	isPrimary: boolean;
	isEvent: boolean;
	isCarFleet: boolean;
}

export interface CompaniesFormValues {
	companies: CompanyFormValues[];
	action: SubmitAction;
}

const emptyCompanyFormValues: CompanyFormValues = {
	company: null,
	position: "",
	isPrimary: false,
	isEvent: false,
	isCarFleet: false,
};

export const emptyCompaniesFormValues: CompaniesFormValues = {
	companies: [],
	action: "next",
};

interface Props extends CreateContactStepForm<CompaniesFormValues> {
	onGoBack: () => void;
}

const handleDelete = (props: FieldArrayRenderProps, index: number) => {
	props.remove(index);
	const remaining = (props.form.values as CompaniesFormValues).companies.filter((_, i) => i !== index);

	if (remaining.length > 0 && !remaining.some((c) => c.isPrimary)) {
		void props.form.setFieldValue(`${props.name}[0].isPrimary`, true);
	}
};

export const CompaniesForm: FC<Props> = ({ onGoBack, initialValues, onSubmit }) => {
	const { t } = useTranslation();

	const validate = useCallback(
		(values: CompaniesFormValues) => {
			const errors: { [K in keyof CompaniesFormValues]?: any } = {};

			values.companies.forEach(({ company }, index) => {
				const companyErrors: { [K in keyof CompanyFormValues]?: any } = {};

				if (!company) {
					companyErrors.company = t("Required field");
				}

				if (Object.keys(companyErrors).length > 0) {
					errors.companies = { ...(errors.companies || {}), [index]: companyErrors };
				}
			});

			return errors;
		},
		[t],
	);

	return (
		<Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
			<DrawerFormWithSubmitter>
				<DrawerBody>
					<FieldArray name="companies">
						{(props) => (
							<>
								<AdditionalList>
									{(props.form.values as CompaniesFormValues).companies.map((_, index) => (
										<AdditionalListItem key={index} onDelete={() => handleDelete(props, index)}>
											<CreatableCompanySelect
												name={`${props.name}[${index}].company`}
												label={t("Additional company")}
												required
											/>
											<TextField name={`${props.name}[${index}].position`} label={t("Position")} />
											<CheckboxField
												label={t("Mark as primary")}
												name={`${props.name}[${index}].isPrimary`}
												disabled={(props.form.values as CompaniesFormValues).companies.length === 1}
												onChange={() => {
													props.form.values[props.name].forEach((_: any, i: number) => {
														void props.form.setFieldValue(`${props.name}[${i}].isPrimary`, false);
													});

													void props.form.setFieldValue(`${props.name}[${index}].isPrimary`, true);
												}}
											/>
											<CheckboxField label={t("Event contact")} name={`${props.name}[${index}].isEvent`} />
											<CheckboxField label={t("Car fleet contact")} name={`${props.name}[${index}].isCarFleet`} />
										</AdditionalListItem>
									))}
								</AdditionalList>
								{(props.form.values as CompaniesFormValues).companies.length === 0 && (
									<EmptyAdditionalList>{t("No additional companies available.")}</EmptyAdditionalList>
								)}
								<Button
									type="button"
									onClick={() =>
										props.push({
											...emptyCompanyFormValues,
											isPrimary: (props.form.values as CompaniesFormValues).companies.length === 0,
										})
									}
									size="small"
									prefix={<Icon $icon="plus" />}
								>
									{t("Add additional company")}
								</Button>
							</>
						)}
					</FieldArray>
				</DrawerBody>
				<DrawerFooter>
					<span />
					<FormActions>
						<Button type="button" onClick={onGoBack}>
							{t("Back")}
						</Button>
						<SubmitButton name="action" value="save" color="default">
							{t("Save without optionals")}
						</SubmitButton>
						<SubmitButton name="action" value="next">
							{t("Next")}
						</SubmitButton>
					</FormActions>
				</DrawerFooter>
			</DrawerFormWithSubmitter>
		</Formik>
	);
};
